import { useState, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { STEP_TYPES } from '../constants.js';

// ── Field helpers ─────────────────────────────────────────────────────────────

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label className="field-label">{label}</label>
      {hint && <p style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginBottom: 6 }}>{hint}</p>}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, mono }) {
  return (
    <input
      className="field-input"
      style={mono ? { fontFamily: 'IBM Plex Mono', fontSize: '.82rem' } : {}}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select className="field-input" value={value ?? ''} onChange={e => onChange(e.target.value)}>
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Textarea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      className="field-input"
      style={{ resize: 'vertical', minHeight: rows * 22 + 16 }}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
    />
  );
}

function CodeEditor({ value, onChange }) {
  return (
    <textarea
      className="code-editor"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      spellCheck={false}
      onKeyDown={e => {
        // Tab → 2 spaces
        if (e.key === 'Tab') {
          e.preventDefault();
          const el = e.target;
          const { selectionStart: s, selectionEnd: end } = el;
          const newVal = el.value.slice(0, s) + '  ' + el.value.slice(end);
          onChange(newVal);
          requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = s + 2; });
        }
      }}
    />
  );
}

// ── Config forms per step type ────────────────────────────────────────────────

function HttpRequestForm({ config, onChange }) {
  function set(key, val) { onChange({ ...config, [key]: val }); }
  const [showHeaders, setShowHeaders] = useState(false);

  const headersStr = typeof config.headers === 'object'
    ? JSON.stringify(config.headers, null, 2)
    : config.headers ?? '{}';

  return (
    <>
      <Field label="URL" hint="Supports template expressions: {{steps.trigger.data.url}}">
        <TextInput value={config.url} onChange={v => set('url', v)} placeholder="https://api.example.com/endpoint" mono />
      </Field>

      <Field label="Method">
        <Select
          value={config.method ?? 'GET'}
          onChange={v => set('method', v)}
          options={['GET','POST','PUT','PATCH','DELETE','HEAD'].map(m => ({ value: m, label: m }))}
        />
      </Field>

      <Field label="Body (JSON)" hint="Only sent for POST, PUT, PATCH. Supports templates.">
        <Textarea
          value={typeof config.body === 'object' ? JSON.stringify(config.body, null, 2) : config.body}
          onChange={v => set('body', v)}
          placeholder='{ "key": "{{steps.trigger.data.id}}" }'
          rows={4}
        />
      </Field>

      <div>
        <button
          onClick={() => setShowHeaders(p => !p)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '.8rem', padding: 0, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}
        >
          <ChevronDown size={14} style={{ transform: showHeaders ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
          Custom Headers
        </button>

        {showHeaders && (
          <Field label="Headers (JSON object)">
            <Textarea
              value={headersStr}
              onChange={v => {
                try { set('headers', JSON.parse(v)); } catch { set('headers', v); }
              }}
              placeholder='{"Authorization": "Bearer {{steps.trigger.data.token}}"}'
              rows={4}
            />
          </Field>
        )}
      </div>
    </>
  );
}

function CodeForm({ config, onChange }) {
  return (
    <Field
      label="JavaScript"
      hint="Access previous steps via `steps.stepId.output`. Return an object to pass data forward."
    >
      <CodeEditor
        value={config.code}
        onChange={v => onChange({ ...config, code: v })}
      />
      <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(129,140,248,.07)', borderRadius: 6, border: '1px solid rgba(129,140,248,.15)' }}>
        <code style={{ fontSize: '.72rem', color: 'var(--primary)', fontFamily: 'IBM Plex Mono', display: 'block', lineHeight: 1.7 }}>
          // Available (runs sandboxed, synchronously):<br/>
          // steps.trigger.data  — trigger payload<br/>
          // steps.myStep.output — previous step result<br/>
          // console.log(…)      — captured to run logs<br/>
          // No fetch/await — use an HTTP Request step for network calls
        </code>
      </div>
    </Field>
  );
}

function FilterForm({ config, onChange }) {
  return (
    <Field
      label="Condition (JavaScript expression)"
      hint="Must evaluate to a truthy value or the workflow stops."
    >
      <CodeEditor
        value={config.condition}
        onChange={v => onChange({ ...config, condition: v })}
      />
      <div style={{ marginTop: 8, fontSize: '.75rem', color: 'var(--text-muted)' }}>
        Example: <code style={{ fontFamily: 'IBM Plex Mono', color: 'var(--amber)' }}>
          steps.trigger.data.amount &gt; 100
        </code>
      </div>
    </Field>
  );
}

function SlackForm({ config, onChange }) {
  function set(key, val) { onChange({ ...config, [key]: val }); }
  return (
    <>
      <Field label="Incoming Webhook URL" hint="From api.slack.com/apps → Incoming Webhooks">
        <TextInput value={config.webhook_url} onChange={v => set('webhook_url', v)} placeholder="https://hooks.slack.com/services/..." mono />
      </Field>
      <Field label="Message" hint="Supports templates: {{steps.trigger.data.text}}">
        <Textarea value={config.message} onChange={v => set('message', v)} placeholder="New event received!" rows={3} />
      </Field>
      <Field label="Channel (optional)" hint="Override the default channel, e.g. #alerts">
        <TextInput value={config.channel} onChange={v => set('channel', v)} placeholder="#alerts" />
      </Field>
      <Field label="Username">
        <TextInput value={config.username} onChange={v => set('username', v)} placeholder="Workflow Bot" />
      </Field>
    </>
  );
}

function GmailForm({ config, onChange }) {
  function set(key, val) { onChange({ ...config, [key]: val }); }
  return (
    <>
      <div style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '.78rem', color: '#FDA4A4', lineHeight: 1.5 }}>
        Requires a Google OAuth 2.0 access token with the <code style={{ fontFamily: 'IBM Plex Mono' }}>gmail.send</code> scope. Tokens expire — use a refresh-token flow in production.
      </div>
      <Field label="Access Token">
        <TextInput value={config.access_token} onChange={v => set('access_token', v)} placeholder="ya29.A0..." mono />
      </Field>
      <Field label="To">
        <TextInput value={config.to} onChange={v => set('to', v)} placeholder="recipient@example.com" />
      </Field>
      <Field label="Subject">
        <TextInput value={config.subject} onChange={v => set('subject', v)} placeholder="Hello from your workflow!" />
      </Field>
      <Field label="Body">
        <Textarea value={config.body} onChange={v => set('body', v)} placeholder="Email body. Supports templates." rows={5} />
      </Field>
    </>
  );
}

const CONFIG_FORMS = {
  http_request: HttpRequestForm,
  code:         CodeForm,
  filter:       FilterForm,
  slack:        SlackForm,
  gmail:        GmailForm,
};

// ── Main Panel ────────────────────────────────────────────────────────────────

export default function StepConfigPanel({ step, onChange, onClose }) {
  const [localConfig, setLocalConfig] = useState(step.config ?? {});
  const [localName,   setLocalName]   = useState(step.name ?? '');

  // Sync on step change
  useEffect(() => {
    setLocalConfig(step.config ?? {});
    setLocalName(step.name ?? '');
  }, [step.id]);

  function commit() {
    onChange({ ...step, name: localName, config: localConfig });
  }

  const meta     = STEP_TYPES[step.type] ?? {};
  const FormComp = CONFIG_FORMS[step.type];

  return (
    <div
      className="slide-in"
      style={{
        width: 360,
        background: 'var(--surface)',
        borderLeft: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        height: '100%',
        flexShrink: 0,
      }}
    >
      {/* Panel header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '.7rem', color: meta.color ?? 'var(--primary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>
            {meta.label ?? step.type}
          </div>
          <div style={{ fontFamily: 'IBM Plex Mono', fontSize: '.85rem', fontWeight: 600, color: 'var(--text)' }}>
            Configure step
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
          <X size={16} />
        </button>
      </div>

      {/* Step name */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
        <label className="field-label">Step name</label>
        <input
          className="field-input"
          value={localName}
          onChange={e => setLocalName(e.target.value)}
          onBlur={commit}
          placeholder="My step"
        />
      </div>

      {/* Config form */}
      <div className="panel-scroll" style={{ flex: 1, padding: '16px' }}>
        {FormComp
          ? <FormComp config={localConfig} onChange={cfg => { setLocalConfig(cfg); }} />
          : <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>No configuration for this step type.</p>
        }
      </div>

      {/* Footer */}
      <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)' }}>
        <button className="btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={commit}>
          Apply Changes
        </button>
      </div>
    </div>
  );
}
